import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { type NotificationEvent } from '@fp3/shared'
import { useBrowserNotifications } from './useBrowserNotifications'

export const useNotificationsStream = (): void => {
  const queryClient = useQueryClient()
  const showBrowserNotification = useBrowserNotifications()
  const [token, setToken] = useState(() => localStorage.getItem('fp3_auth_token'))

  useEffect(() => {
    const onTokenRefresh = () => setToken(localStorage.getItem('fp3_auth_token'))
    // same-tab refresh emits a custom event; other tabs update localStorage directly,
    // which fires the native storage event only in non-authoring tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'fp3_auth_token') onTokenRefresh()
    }
    window.addEventListener('fp3:token-refreshed', onTokenRefresh)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('fp3:token-refreshed', onTokenRefresh)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    if (!token) return

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'
    const es = new EventSource(`${apiUrl}/notifications/stream?token=${token}`)

    es.onmessage = (event: MessageEvent<string>) => {
      let payload: NotificationEvent
      try {
        payload = JSON.parse(event.data) as NotificationEvent
      } catch {
        return
      }

      switch (payload.type) {
        case 'created':
          void queryClient.invalidateQueries({ queryKey: ['notifications'] })
          showBrowserNotification(payload.notification)
          break

        case 'read':
        case 'unread':
        case 'read-all':
        case 'deleted':
        case 'deleted-read':
          void queryClient.invalidateQueries({ queryKey: ['notifications'] })
          break

        case 'unread-count':
          queryClient.setQueryData(['notifications', 'unread-count'], {
            count: payload.count,
          })
          break

        case 'ping':
          break
      }
    }

    let pendingReconnectSync = false
    es.onerror = () => {
      // EventSource auto-reconnects; resync unread count once connection re-opens
      if (!pendingReconnectSync) {
        pendingReconnectSync = true
        es.addEventListener(
          'open',
          () => {
            pendingReconnectSync = false
            void queryClient.invalidateQueries({ queryKey: ['notifications'] })
          },
          { once: true }
        )
      }
    }

    return () => {
      es.close()
    }
  }, [token, queryClient, showBrowserNotification])
}
